// "Install app": Chrome, Edge and Android offer an install prompt through
// beforeinstallprompt, which fires once early, so it is caught as soon as
// this module loads (main.tsx imports it before rendering). iOS Safari has no
// prompt: people add the app from the Share menu.
import type { AppInstall } from '@ims/app'

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((l) => l())

const standalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true

window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault() // we show our own button instead of the mini-infobar
  deferred = event as InstallPromptEvent
  notify()
})
window.addEventListener('appinstalled', () => {
  deferred = null
  notify()
})
window.matchMedia('(display-mode: standalone)').addEventListener('change', notify)

const isIos =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const browserInstall: AppInstall = {
  available: () => deferred !== null,
  installed: standalone,
  manualHint: () =>
    isIos && !standalone() ? 'On iPhone or iPad: tap Share, then “Add to Home Screen”.' : null,
  async prompt() {
    const event = deferred
    if (!event) return 'unavailable'
    deferred = null
    await event.prompt()
    const { outcome } = await event.userChoice
    notify()
    return outcome
  },
  subscribe(listener) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
}
